import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteShell from '@/components/feature/SiteShell';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import DemoPlayer from './components/DemoPlayer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { comments as allComments } from '@/mocks/comments';
import { categoryMap, formatDate, getMovieById, getRecommendations } from '@/lib/catalog';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id);
  const [favorited, setFavorited] = useState(false);
  const [toast, setToast] = useState('');

  const recommendations = useMemo(() => (movie ? getRecommendations(movie, 5) : []), [movie]);
  const moreLikeThis = useMemo(() => (movie ? getRecommendations(movie, 6) : []), [movie]);
  const movieComments = useMemo(
    () => (movie ? allComments.filter((comment) => comment.movieId === movie.id) : []),
    [movie],
  );

  usePageMeta({
    title: movie ? `${movie.title} - 影视详情 - 黄牛视频` : '影视详情 - 黄牛视频',
    description: movie
      ? `${movie.synopsis.slice(0, 78)}……${movie.title}（${movie.year}）${movie.genres.join('、')}，导演${movie.director}。`
      : '黄牛视频影视详情页，查看影片简介、主创信息与相关推荐。',
    keywords: movie
      ? `${movie.title},${movie.genres.join(',')},影视详情,黄牛视频,${movie.director}`
      : '影视详情,黄牛视频',
  });

  if (!movie) {
    return (
      <SiteShell>
        <div className="mx-auto w-full max-w-3xl px-4 py-24 text-center md:px-8">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-400">
            <i className="ri-film-line" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这部作品</h1>
          <p className="mt-3 text-sm text-foreground-500">
            该影视条目可能已下线，或链接地址有误。你可以回到首页继续浏览，也可以直接搜索作品名称。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="rounded-md bg-primary-900 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-800"
            >
              返回首页
            </Link>
            <Link
              to="/search"
              className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-100"
            >
              去搜索作品
            </Link>
          </div>
        </div>
      </SiteShell>
    );
  }

  const category = categoryMap[movie.category];

  const handleShare = async () => {
    const url = `${window.location.origin}${__BASE_PATH__}detail/${movie.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast('详情页链接已复制');
    } catch {
      setToast('复制失败，可手动复制浏览器地址栏链接');
    }
    window.setTimeout(() => setToast(''), 2400);
  };

  return (
    <SiteShell>
      <div className="w-full px-4 pb-2 pt-5 md:px-8 md:pt-7">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category.label, to: `/category/${category.key}` },
            { label: movie.title },
          ]}
        />
      </div>

      <section className="w-full px-4 pb-8 pt-4 md:px-8 md:pb-10">
        <div className="relative overflow-hidden rounded-lg bg-primary-950 px-4 py-6 md:px-8 md:py-9">
          <div className="absolute -right-16 top-0 h-52 w-52 rounded-full bg-accent-500/10 blur-3xl" />
          <div className="relative flex flex-col gap-6 sm:flex-row md:gap-8">
            <div className="w-[150px] shrink-0 sm:w-[180px] md:w-[230px]">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-primary-900">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 主封面`}
                  title={`${movie.title} - ${category.label} - 黄牛视频`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setFavorited((prev) => !prev);
                    setToast(favorited ? '已取消想看' : '已加入想看清单（本地演示）');
                    window.setTimeout(() => setToast(''), 2400);
                  }}
                  className={`flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-2 py-2 text-xs font-medium transition-colors ${
                    favorited
                      ? 'bg-accent-500 text-accent-950'
                      : 'bg-background-50/10 text-background-50 hover:bg-background-50/20'
                  }`}
                >
                  <i className={`${favorited ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`} />
                  {favorited ? '已想看' : '想看'}
                </button>
                <button
                  type="button"
                  aria-label="分享"
                  onClick={handleShare}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-background-50/10 text-background-50 transition-colors hover:bg-background-50/20"
                >
                  <i className="ri-share-forward-line text-sm" />
                </button>
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${category.key}`}
                  className="rounded-full bg-accent-500 px-2.5 py-1 text-[10px] font-bold text-accent-950"
                >
                  {category.label}
                </Link>
                {movie.genres.map((genre) => (
                  <span
                    key={genre}
                    className="rounded-full border border-background-50/25 px-2.5 py-1 text-[10px] text-background-100"
                  >
                    {genre}
                  </span>
                ))}
              </div>

              <h1 className="mt-3 font-heading text-2xl font-extrabold leading-tight text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-2 text-sm text-background-100/85">{movie.tagline}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-baseline gap-1">
                  <span className="font-label text-2xl font-extrabold text-accent-400 md:text-3xl">
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="text-[11px] text-background-100/60">/ 10</span>
                </div>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className={`${
                        star <= Math.round(movie.rating / 2)
                          ? 'ri-star-fill text-accent-400'
                          : 'ri-star-line text-background-50/40'
                      } text-sm`}
                    />
                  ))}
                </div>
                <span className="text-[11px] text-background-100/60">
                  热度 {movie.heat} · {formatDate(movie.updatedAt)}更新
                </span>
              </div>

              <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2 text-xs sm:grid-cols-2">
                {[
                  { label: '导演', value: movie.director },
                  { label: '主演', value: movie.cast.join(' / ') },
                  { label: '类型', value: movie.genres.join(' / ') },
                  { label: '地区', value: movie.region },
                  { label: '上映年份', value: String(movie.year) },
                  { label: '片长 / 集数', value: movie.duration },
                ].map((row) => (
                  <div key={row.label} className="flex gap-2">
                    <dt className="w-16 shrink-0 text-background-100/50">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-background-50/90">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="#player"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-accent-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-play-circle-fill text-base" />
                  播放演示
                </a>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-link" />
                  分享链接
                </button>
                <Link
                  to={`/category/${category.key}`}
                  className="flex items-center gap-1 whitespace-nowrap text-xs text-primary-200 transition-colors hover:text-accent-300"
                >
                  更多{category.label}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>

          {toast && (
            <div className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 animate-fade-in rounded-md bg-background-50 px-4 py-2 text-xs font-medium text-primary-900">
              {toast}
            </div>
          )}
        </div>
      </section>

      <section className="w-full px-4 pb-12 md:px-8 md:pb-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10">
          <div className="min-w-0">
            <div id="player" className="scroll-mt-24">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  演播预览
                </h2>
                <span className="rounded-full bg-background-100 px-2.5 py-1 text-[10px] text-foreground-500">
                  前端演示 · 无真实片源
                </span>
              </div>
              <DemoPlayer title={movie.title} poster={movie.backdrop || movie.poster} />
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">剧情简介</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">{movie.synopsis}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {[...movie.genres, movie.region, `${movie.year}年`].map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100/50 p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950">作品信息</h2>
              <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
                {[
                  { label: '作品名称', value: movie.title },
                  { label: '所属分类', value: category.label },
                  { label: '导演', value: movie.director },
                  { label: '主演', value: movie.cast.join('、') },
                  { label: '地区 / 年份', value: `${movie.region} / ${movie.year}` },
                  { label: '最近更新', value: formatDate(movie.updatedAt) },
                ].map((row) => (
                  <div key={row.label} className="flex gap-2 border-b border-background-200 py-1.5 last:border-b-0">
                    <span className="w-20 shrink-0 text-foreground-500">{row.label}</span>
                    <span className="min-w-0 flex-1 text-foreground-800">{row.value}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-foreground-400">
                以上条目信息为虚构示例数据，仅用于展示页面结构与内容组织方式。
              </p>
            </div>

            <div className="mt-8">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  观众评论
                </h2>
                <span className="text-[11px] text-foreground-400">
                  评论仅展示，暂未开放发布（待接入）
                </span>
              </div>
              {movieComments.length > 0 ? (
                <ul className="mt-4 space-y-3">
                  {movieComments.map((comment) => (
                    <li
                      key={comment.id}
                      className="rounded-lg border border-background-200 bg-background-50 p-3.5"
                    >
                      <div className="flex items-center gap-2.5">
                        <span
                          className={`flex h-8 w-8 items-center justify-center rounded-full font-heading text-xs font-bold ${
                            comment.avatarColor === 'accent'
                              ? 'bg-accent-100 text-accent-800'
                              : comment.avatarColor === 'secondary'
                                ? 'bg-secondary-100 text-secondary-800'
                                : 'bg-primary-100 text-primary-800'
                          }`}
                        >
                          {comment.user.slice(0, 1)}
                        </span>
                        <div>
                          <p className="text-xs font-medium text-foreground-900">{comment.user}</p>
                          <p className="text-[10px] text-foreground-400">{comment.time}</p>
                        </div>
                        <span className="ml-auto flex items-center gap-1 text-[10px] text-foreground-400">
                          <i className="ri-thumb-up-line" />
                          {comment.likes}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                        {comment.content}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100/40 px-4 py-10 text-center">
                  <i className="ri-chat-3-line text-2xl text-foreground-400" />
                  <p className="mt-2 text-xs text-foreground-500">
                    这部作品还没有展示评论，欢迎在功能接入后参与讨论。
                  </p>
                </div>
              )}
            </div>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-heading text-sm font-bold text-foreground-950">相关推荐</h2>
                <span className="text-[10px] text-foreground-400">基于类型标签匹配</span>
              </div>
              <ul className="space-y-3">
                {recommendations.map((item) => (
                  <li key={item.id}>
                    <Link to={`/detail/${item.id}`} className="group flex gap-3">
                      <div className="h-[68px] w-12 shrink-0 overflow-hidden rounded bg-background-200">
                        <img
                          src={item.poster}
                          alt={`${item.title} 封面`}
                          title={`${item.title} - 相关推荐 - 黄牛视频`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium text-foreground-900 group-hover:text-primary-700">
                          {item.title}
                        </p>
                        <p className="mt-1 truncate text-[11px] text-foreground-500">
                          {item.year} · {item.genres.join(' / ')}
                        </p>
                        <p className="mt-1 font-label text-[11px] font-semibold text-accent-600">
                          {item.rating.toFixed(1)} 分
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-primary-900 p-4">
              <p className="font-heading text-sm font-bold text-background-50">所属分类</p>
              <p className="mt-2 text-[11px] leading-relaxed text-primary-200">
                本作品属于「{category.label}」，该频道共有独立的筛选与排序能力。
              </p>
              <Link
                to={`/category/${category.key}`}
                className="mt-3 flex items-center justify-between whitespace-nowrap rounded-md bg-background-50/10 px-3 py-2 text-xs text-background-50 transition-colors hover:bg-background-50/20"
              >
                进入{category.label}频道
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-background-100/60 px-4 py-10 md:px-8 md:py-12">
        <div className="mb-5 flex items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              你可能还喜欢
            </h2>
            <p className="mt-1.5 text-xs text-foreground-500">同分类高分作品，按类型重合度排序。</p>
          </div>
          <Link
            to={`/category/${category.key}`}
            className="whitespace-nowrap text-xs text-foreground-600 hover:text-primary-700"
          >
            更多作品
            <i className="ri-arrow-right-line ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-6">
          {moreLikeThis.map((item) => (
            <MovieCard key={item.id} movie={item} />
          ))}
        </div>
      </section>
    </SiteShell>
  );
}